import React, { useState, useEffect } from 'react';
import Accordion from '@components/accordion/accordion';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import { AccordionBlockProps } from './accordion-block.types';
import { defineResponsiveGridColumns } from '@utils/utils';
import { doesUrlHaveAnchorId, generateKey } from '@utils/utils';
import { H2Atom } from '@atoms/text-atoms/text-atoms';

const AccordionBlock: React.FC<AccordionBlockProps> = ({
  anchorId,
  title,
  items = [],
  greyBackground = false,
  className,
  translate = true,
}) => {
  const [isOpen, setIsOpen] = useState<string | null>(null);
  const [urlHasAnchorId, setUrlHasAnchorId] = useState(false);

  useEffect(() => {
    const urlAnchorStatus = doesUrlHaveAnchorId(window.location.href, anchorId);
    setUrlHasAnchorId(urlAnchorStatus);
  }, [anchorId]);

  if (items.length > 0) {
    return (
      <div id={anchorId} data-testid="accordion-block" className={className}>
        <div className="styling-frame">
          {title && <H2Atom>{title}</H2Atom>}
          <div className="accordions-container">
            {items.map((item, index) => {
              const key = generateKey(item.title, index);
              return (
                <Accordion
                  key={key}
                  itemId={key}
                  title={item.title}
                  richTextContent={item.richTextContent}
                  richTextTooltipData={item.tooltipData}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  urlHasAnchorId={urlHasAnchorId}
                />
              );
            })}
          </div>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

const StyledAccordionBlock = styled(AccordionBlock)`
  margin-top: ${({ anchorId }) => (anchorId ? vars.spacingL : vars.spacingM)};
  display: grid;
  margin: ${({ greyBackground }) =>
      greyBackground ? vars.spacingXl : vars.spacingM}
    auto;
  column-gap: ${vars.mobileColumnPercentageGap};
  grid-template-columns: ${defineResponsiveGridColumns(4)};

  @media screen and (min-width: ${vars.breakPointTablet}) {
    column-gap: ${vars.standardColumnPercentageGap};
    grid-template-columns: ${defineResponsiveGridColumns(8)};
  }

  @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
    column-gap: ${vars.standardColumnPercentageGap};
    grid-template-columns: ${defineResponsiveGridColumns(12)};
  }

  .accordions-container,
  .styling-frame,
  ${H2Atom} {
    grid-column: col1-start/col4-end;

    @media screen and (min-width: ${vars.breakPointTablet}) {
      grid-column: col2-start/col7-end;
    }

    @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
      grid-column: col3-start/col10-end;
    }
  }

  ${H2Atom} {
    ${vars.fontSize6}
    font-weight: ${vars.fontWeightBold};
    padding: 0;
    &::first-letter {
      text-transform: uppercase;
    }
    margin-bottom: ${vars.spacingS};
  }

  .styling-frame {
    padding: ${({ greyBackground }) =>
      greyBackground
        ? `${vars.spacingM} ${vars.spacingM} ${vars.spacingXs}`
        : `${vars.spacingM} 0`};
    border-radius: 8px;
    background-color: ${({ greyBackground }) =>
      greyBackground ? vars.greyHavens : 'none'};
    border: ${({ greyBackground }) =>
      greyBackground ? `1px ${vars.transparentBorderGrey} solid` : 'none'};
  }
`;

export default StyledAccordionBlock;
